import type { ComponentPropsWithoutRef } from 'react';
import { cn } from '../../../lib/utils';
import { EurecaLogoMark } from '../../../icons/eureca-logo-mark';

export type AppLoadingSplashProps = Omit<ComponentPropsWithoutRef<'div'>, 'children'> & {
  /** Accessible label announced to screen readers. Defaults to `Carregando`. */
  label?: string;
};

/**
 * Full-viewport brand splash shown while an app boots — the global loading
 * skeleton displayed by every SPA's auth gate while Keycloak initialises.
 *
 * Renders the [[EurecaLogoMark]] centred on `bg-background` as a pulsing
 * skeleton — coloured with `text-accent` (the muted skeleton tone, not the
 * vivid brand teal) so it reads as a loading placeholder. It is paired with the
 * inline boot splash injected into each app's `index.html` (same mark, colour
 * and pulse) so there's no flash when React mounts and takes over.
 *
 * Pass `data-testid` to override the default (`app-loading-splash`) when a flow
 * already targets a specific id.
 */
export function AppLoadingSplash({
  label = 'Carregando',
  className,
  ...props
}: AppLoadingSplashProps) {
  return (
    <div
      role="status"
      aria-label={label}
      data-testid="app-loading-splash"
      className={cn('flex min-h-dvh items-center justify-center bg-background', className)}
      {...props}
    >
      <EurecaLogoMark className="h-10 w-auto animate-pulse text-accent [animation-duration:1s]" />
      <span className="sr-only">{label}</span>
    </div>
  );
}
